import { toIntlLocale } from "@/i18n/to-intl-locale";
import { toCurrency } from "@/shared/utils/currencies";
import type { ControlParameterResponseDto } from "@/spz-api/model";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

export const useTranslateControlWithParameters = (
  control: { name: string; currency: string },
  parameters: ControlParameterResponseDto[],
) => {
  const { t, i18n } = useTranslation();

  const formatCellValue = useCallback(
    (
      value: ControlParameterResponseDto["value"],
      type: ControlParameterResponseDto["type"],
    ) => {
      switch (type) {
        case "monetary":
          return Intl.NumberFormat(toIntlLocale(i18n.language), {
            style: "currency",
            currency: toCurrency(control.currency),
          }).format(Number(value));
        case "percentage":
          return Intl.NumberFormat(toIntlLocale(i18n.language), {
            style: "percent",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(Number(value));
        default:
          return value.toString();
      }
    },
    [i18n.language, control.currency],
  );

  const translatedParameters = useMemo(() => {
    return parameters.reduce(
      (acc, parameter) => {
        acc[parameter.name] = formatCellValue(parameter.value, parameter.type);
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [parameters, formatCellValue]);

  return t(control.name, translatedParameters);
};
